// g2-controller-review-session-exemption.ts
//
// P20 exemption predicate split out of feature-dispatch-gate-guard.ts to keep
// that file ≤200 lines. Leaf module: only needs the reserved ticket id
// constant and the guard's input shape (type-only import — no cycle back to
// feature-dispatch-gate-guard.ts).
import { isControllerReservedTicketId } from "./controller-reserved-ticket-ids.js";
import type { EvaluateFeatureDispatchGateGuardInput } from "./feature-dispatch-gate-guard.js";

/**
 * P20: the candidate-review session is exempt from G2 — and ONLY from G2.
 *
 * Why an exemption is needed at all (measured on real mission
 * ME-2026-dc845a): the session's kind is `review`, so the guard demanded G2;
 * `g2_waive_delegated_by_g0` defaults to false, so G2 sits `pending` on every
 * mission by default. The review that gates the release could therefore never
 * dispatch — the run burned two worker sessions before stalling.
 *
 * Why it is keyed on id + empty scope rather than on the KIND: a plan worker
 * can author a ticket of kind `review`, so exempting the kind would hand it a
 * way to dodge G2 entirely — the Bug K shape (worker declaring the constraint
 * that governs itself). Both conditions here come from
 * review-candidate-session.ts alone: the reserved id, and `allowed_paths: []`,
 * which makes commit-owner stage nothing so the session cannot change the
 * candidate it was asked to review.
 *
 * G2 is a gate about the PLAN's size and risk (spec §8). This session is not
 * part of the plan and writes no product code, so G2 has nothing to protect
 * here. G0/G1 — the mission's authority to act at all — still apply.
 *
 * Re-review 2026-09-26 (C2-F2): keyed on EVERY controller-reserved id, not
 * just T-9001 — the second reviewer (T-9002, review-clean-v1) is the same
 * read-only controller session and used to stop on `gate_not_approved`
 * whenever G2 was pending/invalidated between the two review sessions.
 */
export function isControllerOwnedReadOnlySession(
  input: EvaluateFeatureDispatchGateGuardInput,
): boolean {
  return (
    input.ticketId !== undefined &&
    isControllerReservedTicketId(input.ticketId) &&
    input.allowedPaths !== undefined &&
    input.allowedPaths.length === 0
  );
}
